import React from 'react';
import PropTypes from 'prop-types';
import { offerLine } from '../../utils/offerPricing';

/**
 * The promotional-offer banner shown at the top of a pricelist. SHARED by the senior
 * (PackageChooser) and elementary (OptionSelectionContent) flows — one implementation.
 * `offer` is the coupon resolved by useOfferCoupon / for_landing_page.
 *
 * Headline: the admin-authored `promoHeadline` when set, otherwise a derived line from the
 * coupon ("20% off, $20 credit and free shipping. Use coupon FALL20"). This is where the
 * coupon CODE lives — cards show the discount (a struck-through % price, plus an order-level
 * "$X credit at checkout" line for the flat part) but not the code, so without this banner a
 * percentage coupon with no promoHeadline would strike a price with the code nowhere.
 * Renders nothing only when there's no offer AND nothing derivable.
 *
 * FULL-BLEED — spans the full viewport width (edge to edge) regardless of the centered
 * container it sits in, via the `w-screen` + `margin-left: calc(50% - 50vw)` breakout.
 * STICKY — it scrolls up WITH the packages until it reaches the top of the viewport, then
 * pins there (`sticky top-0`) while the packages keep scrolling up and off underneath it
 * (`z-10` keeps the banner above the cards). The customer nav is in-flow (not fixed), so
 * `top-0` pins to the true viewport top after the nav scrolls away; the app shell uses
 * `overflow-x: clip` (not `hidden`) specifically so no ancestor traps this sticky element.
 * `className` is just for per-caller spacing (e.g. `mt-2`).
 */
// Delivery-truck glyph (Tabler "truck", inlined so we don't add an icon
// dependency for one use). Shown next to the headline when the coupon
// waives shipping. Substring-matches the coupon_type to agree with the
// Rails Coupon#free_shipping? check. Sized/coloured to sit one step
// behind the offer text in the hierarchy; hidden on <768px where the
// bar is already tight.
const TruckIcon = () => (
  <svg
    className="inline-block max-md:hidden ml-[10px] align-[-6px]"
    width="22" height="22" viewBox="0 0 24 24"
    fill="none" stroke="#B5D4F4" strokeWidth="1.75"
    strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
    <path d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
    <path d="M5 17h-2v-4m-1 -8h11v12m-4 0h6m4 0h2v-6h-8m0 -5h5l3 5" />
    <path d="M3 9l4 0" />
  </svg>
);

export default function PromoBanner({ offer, className }) {
  if (!offer) return null;
  const headline = offer.promoHeadline || offerLine(offer);
  if (!headline) return null;
  const includesFreeShipping = !!offer.couponType && offer.couponType.includes('FREE_SHIPPING');

  return (
    <div
      className={`sticky top-0 z-10 w-screen ml-[calc(50%_-_50vw)] px-5 py-2.5 text-center shadow-md ${className}`}
      style={{ background: 'linear-gradient(90deg, #0C447C, #14608F)' }}
    >
      <p className="text-base sm:text-lg font-extrabold text-white uppercase tracking-widest">
        {headline}
        {includesFreeShipping && <TruckIcon />}
      </p>
      {offer.promoSubhead && (
        <p className="mt-0.5 text-xs font-medium text-white uppercase tracking-widest">{offer.promoSubhead}</p>
      )}
    </div>
  );
}

PromoBanner.defaultProps = { offer: null, className: 'mt-2' };

PromoBanner.propTypes = {
  offer: PropTypes.shape({
    promoHeadline: PropTypes.string,
    promoSubhead: PropTypes.string,
    // Used to derive a headline when promoHeadline is absent (offerLine).
    couponType: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    flatValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
  }),
  // Per-flow width + corner-radius so the banner matches that flow's cards.
  className: PropTypes.string,
};
